/**
 * @file 牌局状态机：`createGame` / `getLegalActions` / `applyAction`。
 *
 * ## 三条贯穿全文件的实现约定
 *
 * 1. **`wall` 只装未摸的牌。**
 *    `assertStateInvariants()` 遍历的是**整个** `state.wall` 数组，而不是
 *    `wall.slice(wallCursor)`。因此摸牌必须把牌从数组里**移除**，否则同一张牌
 *    会同时出现在牌墙与手牌中，实体守恒当场失败。
 *    于是 `wallCursor` 在本实现里退化为「累计已摸/已发张数」的审计计数器，
 *    剩余张数一律以 `wall.length - reservedTiles` 为准（见 {@link wallRemaining}）。
 *
 * 2. **点炮张不搬家。**
 *    `05` §4.3 规定一炮多响时真实牌实体留在点炮者的弃牌河，其余胡家记虚拟牌。
 *    为使单响与多响走同一条代码路径（也为了让守恒检查天然成立），本实现里
 *    **任何被胡的弃牌都不进胡家手牌**——胡家手牌冻结在 `3n+1`，
 *    `WinRecord.winningTile` 记录那张牌的 id。自摸则相反：胡牌张本就在手中，
 *    手牌冻结在 `3n+2`。
 *
 * 3. **杠上花靠 `atTurn` 推导，不存标志位。**
 *    开杠时把 `meld.atTurn` 写成「补摸后」的 `turnCount`，
 *    `isGangFlower()` 比对二者即可，省掉一个「忘了清除」的 bug 源。
 *
 * 纯函数、平台无关：不读时钟、不取随机源，`now` 与 `seed` 一律由宿主注入。
 */

import type {
  Action,
  ApplyResult,
  Audience,
  ClaimActionType,
  ClaimOption,
  GameEvent,
  GameEventType,
  GameState,
  Meld,
  PlayerState,
  SeatIndex,
  Seats,
  Suit,
  TileId,
  TileKind,
  TingCandidate,
  WinKind,
  WinRecord,
} from './types';
import { SUITS } from './types';
import type { CreateGameOptions, EngineContext } from './index';
import type { RejectReason } from './errors';
import { InvariantViolationError } from './errors';
import {
  createOrderedDeck,
  keyOfKind,
  kindFromKindIndex,
  kindIndexFromTileId,
  kindOfTileId,
  suitToIndex,
} from './tiles';
import { createRngState, shuffleInPlace } from './prng';
import {
  autoPickQue,
  expectedHandSize,
  handCounts,
  hasQueTile,
  isGangFlower,
  nextActiveSeat,
  queTilesOf,
  removeTile,
  sortedInsert,
  takeKind,
} from './hand';
import { canWinCounts, getWaits } from './win';
import { maxPossibleFan } from './scoring';
import { checkWin } from './check-win';
import { evaluateTing } from './ting';
import { calculateScore, endReasonOf, liveNetScores } from './settlement';
import { assertStateInvariants } from './invariants';

/* ═══════════════════════ 事件发射 ═══════════════════════ */

/** 一次 `applyAction` 调用内共享的事件收集器。 */
interface Emit {
  /** 本次调用产出的新 `seq`；所有事件都打这个号。 */
  readonly seq: number;
  /** 宿主注入的时间戳。 */
  readonly at: number;
  readonly out: GameEvent[];
}

const ALL: Audience = { scope: 'all' };
const toSeat = (seat: SeatIndex): Audience => ({ scope: 'seat', seat });
const toOthers = (except: SeatIndex): Audience => ({ scope: 'others', except });

function emit(
  e: Emit,
  type: GameEventType,
  audience: Audience,
  payload: Readonly<Record<string, unknown>>,
): void {
  e.out.push({ type, seq: e.seq, audience, payload, at: e.at });
}

/* ═══════════════════════ 状态更新原语 ═══════════════════════ */

/** 替换单个座位的状态，返回新 `GameState`（不修改入参）。 */
function withPlayer(state: GameState, seat: SeatIndex, patch: Partial<PlayerState>): GameState {
  const p = state.players;
  const updated: PlayerState = { ...p[seat], ...patch };
  const players: Seats<PlayerState> = [
    seat === 0 ? updated : p[0],
    seat === 1 ? updated : p[1],
    seat === 2 ? updated : p[2],
    seat === 3 ? updated : p[3],
  ];
  return { ...state, players };
}

/** 牌墙可摸张数（扣除留底牌）。这是唯一允许公开的牌墙信息。 */
function wallRemaining(state: GameState): number {
  return Math.max(0, state.wall.length - state.rules.config.wall.reservedTiles);
}

/**
 * 当前是否允许开杠（`02` §4 G2）。
 * 杠必然带一次补摸，牌墙不足时开杠会死锁，故 `forbidGangWhenWallEmpty` 恒为 `true`（V-9）。
 */
function canGangNow(state: GameState): boolean {
  return !state.rules.config.gang.forbidGangWhenWallEmpty || wallRemaining(state) >= 1;
}

/** 该座位的定缺花色序号；未定缺返回 `-1`。 */
function queIndexOf(player: PlayerState): number {
  return player.voidSuit === null ? -1 : suitToIndex(player.voidSuit);
}

/** 把一张牌从某家的弃牌河里取走（被碰/杠走时）。 */
function takeFromRiver(state: GameState, fromSeat: SeatIndex, tileId: TileId): GameState {
  const river = state.players[fromSeat].discards;
  const i = river.lastIndexOf(tileId);
  if (i < 0) {
    throw new InvariantViolationError(`弃牌河中没有该牌: ${tileId}`, { fromSeat, tileId });
  }
  const next = river.slice();
  next.splice(i, 1);
  return withPlayer(state, fromSeat, { discards: next });
}

/** 把 `lastDiscard` 标记为已被吃走。 */
function markClaimed(state: GameState): GameState {
  if (state.lastDiscard === null) return state;
  return { ...state, lastDiscard: { ...state.lastDiscard, claimed: true } };
}

/**
 * 用 {@link liveNetScores} 刷新四家 `score`。
 *
 * 让实时分与终局结算共用 `buildLedger()` 这一套算法，是「累加值 ≠ 重算值」
 * 这类经典结算 bug 的结构性解法：全局只有一个算分入口。
 */
function applyLiveScores(state: GameState): GameState {
  const net = liveNetScores(state);
  const p = state.players;
  const players: Seats<PlayerState> = [
    p[0].score === net[0] ? p[0] : { ...p[0], score: net[0] },
    p[1].score === net[1] ? p[1] : { ...p[1], score: net[1] },
    p[2].score === net[2] ? p[2] : { ...p[2], score: net[2] },
    p[3].score === net[3] ? p[3] : { ...p[3], score: net[3] },
  ];
  return { ...state, players };
}

/* ═══════════════════════ 听牌刷新 ═══════════════════════ */

/** 某牌面在该座位视角下的理论剩余张数（服务端全知，用于听口提示）。 */
function remainingOfKind(state: GameState, seat: SeatIndex, kind: TileKind): number {
  const k = keyOfKind(kind);
  let seen = 0;
  for (const id of state.players[seat].hand) if (kindIndexFromTileId(id) === k) seen++;
  for (const p of state.players) {
    for (const id of p.discards) if (kindIndexFromTileId(id) === k) seen++;
    for (const m of p.melds) for (const id of m.tiles) if (kindIndexFromTileId(id) === k) seen++;
  }
  return Math.max(0, 4 - seen);
}

/** 若胡该听口，牌型番数是多少（不含自摸/杠上花等情境番）。 */
function fanIfWin(state: GameState, seat: SeatIndex, kind: TileKind): number {
  const p = state.players[seat];
  const counts = handCounts(p.hand);
  const k = keyOfKind(kind);
  counts[k] = (counts[k] ?? 0) + 1;
  return maxPossibleFan(counts, p.melds, state.rules.config, p.melds.length);
}

/**
 * 刷新某座位的听牌状态。
 *
 * 只在手牌为 `3n+1`（已出牌、等他家打）时计算——`3n+2` 形态下 `evaluateTing`
 * 返回的是「打一张后能听什么」的并集，语义是 UI 提示而非「已成叫」，
 * 写进 `PlayerState.isTing` 会让查大叫口径当场错位。
 *
 * D-13：`ting_changed` 只单播本人，绝不广播。
 */
function refreshTing(state: GameState, seat: SeatIndex, e: Emit): GameState {
  const player = state.players[seat];
  if (player.hasWon) return state;
  if (player.hand.length % 3 !== 1) return state;

  const { isTing, candidates } = evaluateTing(state, seat);
  const tingCandidates: readonly TingCandidate[] = candidates.map((kind) => ({
    kind,
    remaining: remainingOfKind(state, seat, kind),
    estimatedFan: fanIfWin(state, seat, kind),
  }));

  const next = withPlayer(state, seat, { isTing, tingCandidates });
  if (isTing !== player.isTing) {
    emit(e, 'ting_changed', toSeat(seat), { seat, isTing, candidates });
  }
  return next;
}

/* ═══════════════════════ createGame ═══════════════════════ */

/**
 * 创建并发牌，推进到第一个需要玩家输入的阶段。
 *
 * M1 的换三张变体被 V-8 锁死（`variant.huanSanZhang.enabled === false`），
 * 因此发完牌直接进 `void`（定缺）。庄家 14 张、闲家 13 张——庄家进入
 * `playing` 后无需摸牌，直接出牌。
 */
export function createGame(options: CreateGameOptions): ApplyResult {
  const cfg = options.rules.config;
  if (cfg.variant.huanSanZhang.enabled) {
    throw new InvariantViolationError('换三张变体在 M1 未实现（应由 V-8 在建房时拦截）', {
      gameId: options.gameId,
    });
  }

  const shuffled = shuffleInPlace(createOrderedDeck(), createRngState(options.seed));
  const deck = shuffled.result;

  const hands: [TileId[], TileId[], TileId[], TileId[]] = [[], [], [], []];
  let cursor = 0;
  const take = (): TileId => {
    const id = deck[cursor];
    if (id === undefined) {
      throw new InvariantViolationError('发牌时牌墙耗尽', { cursor });
    }
    cursor++;
    return id;
  };
  // 发牌顺序：自庄家起逆时针每人 13 张，最后庄家补第 14 张。
  for (let round = 0; round < 13; round++) {
    for (let i = 0; i < 4; i++) {
      hands[((options.dealer + i) % 4) as SeatIndex].push(take());
    }
  }
  hands[options.dealer].push(take());

  const mk = (seat: SeatIndex): PlayerState => ({
    seat,
    playerId: options.seats[seat].playerId,
    hand: hands[seat].slice().sort((a, b) => a - b),
    melds: [],
    discards: [],
    voidSuit: null,
    isTing: false,
    tingCandidates: null,
    hasWon: false,
    winRecords: [],
    isFlowerPig: false,
    score: 0,
    connected: true,
    isRobot: options.seats[seat].isRobot,
  });

  const state: GameState = {
    gameId: options.gameId,
    seq: 0,
    phase: 'void',
    rules: options.rules,
    players: [mk(0), mk(1), mk(2), mk(3)],
    dealer: options.dealer,
    turn: options.dealer,
    wall: deck.slice(cursor),
    wallCursor: cursor,
    swapDirection: null,
    lastDiscard: null,
    pendingClaims: null,
    rng: shuffled.state,
    turnCount: 0,
    deadlineAt: options.now + cfg.timeout.decideQueMs,
    winnersInOrder: [],
    settlement: null,
  };

  // 108 张守恒：发牌是唯一凭空构造牌局的地方，错在这里后面全错。
  assertStateInvariants(state);

  const e: Emit = { seq: 0, at: options.now, out: [] };
  emit(e, 'game_started', ALL, {
    gameId: state.gameId,
    dealer: state.dealer,
    ruleHash: state.rules.ruleHash,
    wallRemaining: wallRemaining(state),
  });
  for (let s = 0 as SeatIndex; s < 4; s = (s + 1) as SeatIndex) {
    emit(e, 'dealt', toSeat(s), { seat: s, hand: state.players[s].hand });
  }
  emit(e, 'dealt', ALL, {
    // ⚠ 键名不得用 hand/hands/handTiles——那是公开广播的禁字段（见 view.ts）。
    handCounts: [
      state.players[0].hand.length,
      state.players[1].hand.length,
      state.players[2].hand.length,
      state.players[3].hand.length,
    ],
    wallRemaining: wallRemaining(state),
    phase: state.phase,
    deadlineAt: state.deadlineAt,
  });

  return { state, events: e.out };
}

/* ═══════════════════════ getLegalActions ═══════════════════════ */

/**
 * 自摸时的「和了张」。
 *
 * 契约里没有 `lastDrawnTile` 字段（M1-T2 已冻结），所以无法还原玩家真正摸到的
 * 那一张。这里退而求其次：取**最小的、移除后剩余手牌仍听该牌面**的手牌 id。
 * 手牌升序 ⇒ 结果确定、可回放。
 *
 * 这个选择只影响 `WinRecord.winningTile` 这一展示/审计字段：番数由完整的 27 维
 * 计数向量算出，与「指定哪张为和了张」无关，分数因此不受影响。
 */
function zimoWinningTile(state: GameState, seat: SeatIndex): TileId | null {
  const p = state.players[seat];
  const counts = handCounts(p.hand);
  if (!canWinCounts(counts, p.melds.length, p.voidSuit)) return null;

  for (const id of p.hand) {
    const k = kindIndexFromTileId(id);
    counts[k] = (counts[k] ?? 0) - 1;
    const hit = getWaits(counts, p.melds.length, p.voidSuit).includes(k);
    counts[k] = (counts[k] ?? 0) + 1;
    if (hit) return id;
  }
  return p.hand[0] ?? null;
}

/** 自家当前可开的杠：暗杠（手中 4 张）+ 巴杠（已碰的刻子 + 第 4 张）。 */
function selfGangKinds(state: GameState, seat: SeatIndex): readonly TileKind[] {
  const p = state.players[seat];
  const counts = handCounts(p.hand);
  const que = queIndexOf(p);
  const out: TileKind[] = [];

  for (let k = 0; k < 27; k++) {
    if (Math.floor(k / 9) === que) continue; // 缺门牌不得成杠（QC-5）
    if ((counts[k] ?? 0) === 4) out.push(kindFromKindIndex(k));
  }
  for (const m of p.melds) {
    if (m.type !== 'peng') continue;
    const k = keyOfKind(m.kind);
    if (Math.floor(k / 9) === que) continue;
    if ((counts[k] ?? 0) >= 1) out.push(m.kind);
  }
  return out;
}

/**
 * 枚举某座位当前的全部合法动作。服务端校验、AI 候选、UI 按钮态共用此函数。
 *
 * **QC-1 强制打缺**在这里落地：只要手牌里还有定缺花色的牌，
 * 合法动作就收窄为「打出缺门牌」——不给碰、不给杠、不给胡。
 */
export function getLegalActions(state: GameState, seat: SeatIndex): readonly Action[] {
  const expectedSeq = state.seq;
  const out: Action[] = [];
  const player = state.players[seat];

  if (state.phase === 'void') {
    if (player.voidSuit === null) {
      for (const suit of SUITS) out.push({ type: 'declare_void', seat, expectedSeq, suit });
    }
    return out;
  }
  if (state.phase !== 'playing') return out;
  if (player.hasWon) return out;

  /* ── 响应窗口 ── */
  const pc = state.pendingClaims;
  if (pc !== null) {
    if (pc.responses[seat] !== undefined) return out;
    const opt = pc.options.find((o) => o.seat === seat);
    if (opt === undefined) return out;
    for (const a of opt.actions) {
      if (a === 'hu') out.push({ type: 'hu', seat, expectedSeq, tileId: pc.tileId });
      else if (a === 'peng') out.push({ type: 'peng', seat, expectedSeq, tileId: pc.tileId });
      else if (a === 'pass') out.push({ type: 'pass', seat, expectedSeq });
      else for (const kind of opt.gangKinds) out.push({ type: 'gang', seat, expectedSeq, kind });
    }
    return out;
  }

  /* ── 自家回合 ── */
  if (state.turn !== seat) return out;

  const full = expectedHandSize(player.melds);
  if (player.hand.length !== full) {
    // 中间态：等待摸牌（正常流程由引擎自动补摸，这里为手动摸牌 UI 留口子）
    if (player.hand.length === full - 1 && wallRemaining(state) >= 1) {
      out.push({ type: 'draw', seat, expectedSeq });
    }
    // QC-1c：定缺已清空时，即使手牌处于 3n+1（full-1）也允许弃牌。
    // 否则「开局即无缺门牌」的玩家在出牌回合只会拿到 ['draw']，discard 凭空消失，
    // 碰/胡/弃的恢复动作被这个等待摸牌的短路分支误杀。
    if (player.hand.length === full - 1 && !hasQueTile(player)) {
      for (const id of player.hand) out.push({ type: 'discard', seat, expectedSeq, tileId: id });
    }
    return out;
  }

  // QC-1：仍持缺门牌 ⇒ 只能打缺门牌
  if (hasQueTile(player)) {
    for (const id of queTilesOf(player)) out.push({ type: 'discard', seat, expectedSeq, tileId: id });
    return out;
  }

  for (const id of player.hand) out.push({ type: 'discard', seat, expectedSeq, tileId: id });
  if (canGangNow(state)) {
    for (const kind of selfGangKinds(state, seat)) out.push({ type: 'gang', seat, expectedSeq, kind });
  }
  const winning = zimoWinningTile(state, seat);
  if (winning !== null) out.push({ type: 'hu', seat, expectedSeq, tileId: winning });

  return out;
}

/* ═══════════════════════ applyAction ═══════════════════════ */

/** 拒绝：状态原样返回 + 一条单播给提交者的 `action_rejected`。**不抛异常。** */
function rejected(
  state: GameState,
  action: Action,
  reason: RejectReason,
  ctx: EngineContext,
): ApplyResult {
  return {
    state,
    events: [
      {
        type: 'action_rejected',
        seq: state.seq,
        audience: toSeat(action.seat),
        payload: { reason, actionType: action.type },
        at: ctx.now,
      },
    ],
  };
}

/**
 * 应用一个动作。**引擎的唯一状态推进入口。**
 *
 * 合法则 `seq` 恰好 +1，并可能连带推进多步（碰后自动轮到碰家出牌、
 * 四家 pass 后自动摸牌、第三家胡牌后自动结算）；非法则返回原状态 + 拒绝事件。
 */
export function applyAction(state: GameState, action: Action, ctx: EngineContext): ApplyResult {
  if (action.expectedSeq !== state.seq) return rejected(state, action, 'stale_seq', ctx);

  const e: Emit = { seq: state.seq + 1, at: ctx.now, out: [] };
  const next =
    state.phase === 'void'
      ? applyVoid(state, action, ctx, e)
      : state.phase === 'playing'
        ? applyPlaying(state, action, ctx, e)
        : 'wrong_phase';

  if (typeof next === 'string') return rejected(state, action, next, ctx);
  return { state: { ...next, seq: e.seq }, events: e.out };
}

/* ─────────────── 定缺阶段 ─────────────── */

/**
 * 定缺（`02` §1）。四家**并行**决策，谁先谁后不影响结果。
 *
 * 过程中只向本人回执，全部定完才统一广播 `void_done`：
 * 逐个广播会泄漏「谁已经想好了」这类节奏信息，而 GDD 要求的是「同时定缺、统一公开」。
 */
function applyVoid(
  state: GameState,
  action: Action,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  if (action.type !== 'declare_void' && action.type !== 'timeout') return 'wrong_phase';

  const seat = action.seat;
  const player = state.players[seat];
  if (player.voidSuit !== null) return 'illegal_action';

  const suit: Suit = action.type === 'declare_void' ? action.suit : autoPickQue(player.hand);
  if (!SUITS.includes(suit)) return 'illegal_action';

  let next = withPlayer(state, seat, { voidSuit: suit });
  emit(e, 'void_declared', toSeat(seat), { seat, suit });

  if (!next.players.every((p) => p.voidSuit !== null)) return next;

  emit(e, 'void_done', ALL, {
    voidSuits: [
      next.players[0].voidSuit,
      next.players[1].voidSuit,
      next.players[2].voidSuit,
      next.players[3].voidSuit,
    ],
  });
  next = {
    ...next,
    phase: 'playing',
    turn: next.dealer,
    // 庄家手上那第 14 张即视为「第 1 回合摸到的牌」，turnCount 从 1 起算。
    turnCount: 1,
    deadlineAt: ctx.now + next.rules.config.timeout.discardMs,
  };
  emit(e, 'turn_changed', ALL, { seat: next.dealer, phase: 'playing' });
  return next;
}

/* ─────────────── 打牌阶段 ─────────────── */

function applyPlaying(
  state: GameState,
  action: Action,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  return state.pendingClaims !== null
    ? applyClaim(state, action, ctx, e)
    : applyTurn(state, action, ctx, e);
}

function applyTurn(
  state: GameState,
  action: Action,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  if (action.type === 'timeout') return applyTurnTimeout(state, ctx, e);

  if (action.seat !== state.turn) return 'not_your_turn';
  const seat = action.seat;
  if (state.players[seat].hasWon) return 'already_won';

  switch (action.type) {
    case 'draw': {
      const player = state.players[seat];
      if (player.hand.length !== expectedHandSize(player.melds) - 1) return 'illegal_action';
      if (wallRemaining(state) < 1) return 'illegal_action';
      return drawTile(state, seat, false, ctx, e);
    }
    case 'discard':
      return doDiscard(state, seat, action.tileId, ctx, e);
    case 'gang':
      return doSelfGang(state, seat, action.kind, ctx, e);
    case 'hu':
      return doZimo(state, seat, action.tileId, ctx, e);
    default:
      return 'illegal_action';
  }
}

/** 出牌超时：优先打缺门牌，否则打手牌最右一张（`07` §2.10）。 */
function applyTurnTimeout(state: GameState, ctx: EngineContext, e: Emit): GameState | RejectReason {
  const seat = state.turn;
  const player = state.players[seat];
  if (player.hand.length !== expectedHandSize(player.melds)) return 'illegal_action';

  const que = queTilesOf(player);
  const pick = que.length > 0 ? que[que.length - 1] : player.hand[player.hand.length - 1];
  if (pick === undefined) return 'illegal_action';
  return doDiscard(state, seat, pick, ctx, e);
}

/* ─────────────── 摸牌 ─────────────── */

/**
 * 摸一张牌。调用前必须保证 `wallRemaining >= 1`。
 *
 * @param fromTail 杠后补摸从牌尾取（`gang.drawFromTail`，成都主流为 `true`）
 */
function drawTile(
  state: GameState,
  seat: SeatIndex,
  fromTail: boolean,
  ctx: EngineContext,
  e: Emit,
): GameState {
  const wall = state.wall;
  const tileId = fromTail ? wall[wall.length - 1] : wall[0];
  if (tileId === undefined) {
    throw new InvariantViolationError('牌墙已空却仍尝试摸牌', { seat, fromTail });
  }

  const player = state.players[seat];
  let s = withPlayer(state, seat, { hand: sortedInsert(player.hand, tileId) });
  s = {
    ...s,
    wall: fromTail ? wall.slice(0, -1) : wall.slice(1),
    wallCursor: s.wallCursor + 1,
    turnCount: s.turnCount + 1,
    turn: seat,
    deadlineAt: ctx.now + s.rules.config.timeout.discardMs,
  };

  emit(e, 'drawn', toSeat(seat), { seat, tileId, wallRemaining: wallRemaining(s) });
  emit(e, 'drawn', toOthers(seat), { seat, wallRemaining: wallRemaining(s) });
  return s;
}

/** 轮转到 `fromSeat` 的下一个未胡玩家并摸牌；牌墙摸尽或无人可行动则进入结算。 */
function advanceTurnFrom(
  state: GameState,
  fromSeat: SeatIndex,
  ctx: EngineContext,
  e: Emit,
): GameState {
  const cleared: GameState = { ...state, pendingClaims: null };
  const next = nextActiveSeat(cleared.players, fromSeat);
  if (next === null) return enterSettlement(cleared, e);
  if (wallRemaining(cleared) < 1) return enterSettlement(cleared, e);

  const s = drawTile({ ...cleared, turn: next }, next, false, ctx, e);
  emit(e, 'turn_changed', ALL, { seat: next });
  return s;
}

/* ─────────────── 出牌 ─────────────── */

function doDiscard(
  state: GameState,
  seat: SeatIndex,
  tileId: TileId,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  const player = state.players[seat];
  if (player.hand.length !== expectedHandSize(player.melds)) return 'illegal_action';

  // QC-1：仍持缺门牌时只能打缺门牌
  if (player.voidSuit !== null && hasQueTile(player)) {
    if (Math.floor(kindIndexFromTileId(tileId) / 9) !== suitToIndex(player.voidSuit)) {
      return 'void_suit_violation';
    }
  }
  const hand = removeTile(player.hand, tileId);
  if (hand === null) return 'tile_not_owned';

  let next = withPlayer(state, seat, { hand, discards: [...player.discards, tileId] });
  next = {
    ...next,
    lastDiscard: { tileId, seat, atTurn: next.turnCount, claimed: false },
    deadlineAt: null,
  };
  next = refreshTing(next, seat, e);
  emit(e, 'discarded', ALL, { seat, tileId });

  const options = buildDiscardClaimOptions(next, seat, tileId);
  if (options.length === 0) return advanceTurnFrom(next, seat, ctx, e);

  const deadlineAt = ctx.now + next.rules.config.timeout.responseMs;
  next = {
    ...next,
    pendingClaims: { tileId, fromSeat: seat, trigger: 'discard', options, responses: {}, deadlineAt },
    deadlineAt,
  };
  openClaimWindow(next, options, e);
  return next;
}

/**
 * 广播「窗口开了」+ 单播「你能做什么」。
 *
 * 两条事件必须分开：候选集（谁能碰）等价于他人手牌信息，
 * 广播它就等于告诉全场「座位 2 手里有一对刚打出的牌」。
 */
function openClaimWindow(state: GameState, options: readonly ClaimOption[], e: Emit): void {
  const pc = state.pendingClaims;
  if (pc === null) return;
  emit(e, 'claim_window_opened', ALL, {
    fromSeat: pc.fromSeat,
    tileId: pc.tileId,
    trigger: pc.trigger,
    deadlineAt: pc.deadlineAt,
  });
  for (const o of options) {
    emit(e, 'claim_options', toSeat(o.seat), {
      tileId: pc.tileId,
      fromSeat: pc.fromSeat,
      trigger: pc.trigger,
      actions: o.actions,
      gangKinds: o.gangKinds,
      deadlineAt: pc.deadlineAt,
    });
  }
}

/** 弃牌可被谁响应（`02` §3/§4/§6）。 */
function buildDiscardClaimOptions(
  state: GameState,
  fromSeat: SeatIndex,
  tileId: TileId,
): readonly ClaimOption[] {
  const kind = kindOfTileId(tileId);
  const k = kindIndexFromTileId(tileId);
  // 杠上炮：出牌者是刚补摸完就打出来的
  const gangPao = isGangFlower(state.players[fromSeat], state.turnCount);
  const out: ClaimOption[] = [];

  for (let s = 0 as SeatIndex; s < 4; s = (s + 1) as SeatIndex) {
    if (s === fromSeat) continue;
    const p = state.players[s];
    if (p.hasWon) continue;

    const actions: ClaimActionType[] = [];
    const gangKinds: TileKind[] = [];

    const win = checkWin({
      hand: p.hand,
      melds: p.melds,
      winningTile: tileId,
      winKind: gangPao ? 'gang_shang_pao' : 'dianpao',
      voidSuit: p.voidSuit,
      rules: state.rules,
      isLastTile: wallRemaining(state) === 0,
      gen: null,
    });
    if (win.isWin) actions.push('hu');

    // QC-2~QC-5：未清缺不得副露；缺门牌不得碰杠
    if (!hasQueTile(p) && Math.floor(k / 9) !== queIndexOf(p)) {
      const c = handCounts(p.hand)[k] ?? 0;
      if (c >= 2) actions.push('peng');
      if (c >= 3 && canGangNow(state)) {
        actions.push('gang');
        gangKinds.push(kind);
      }
    }

    if (actions.length === 0) continue;
    actions.push('pass');
    out.push({ seat: s, actions, gangKinds });
  }
  return out;
}

/* ─────────────── 自家杠 ─────────────── */

function doSelfGang(
  state: GameState,
  seat: SeatIndex,
  kind: TileKind,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  const player = state.players[seat];
  if (player.hand.length !== expectedHandSize(player.melds)) return 'illegal_action';
  if (hasQueTile(player)) return 'void_suit_violation'; // QC-4：未清缺不得杠
  const k = keyOfKind(kind);
  if (Math.floor(k / 9) === queIndexOf(player)) return 'void_suit_violation'; // QC-5
  if (!canGangNow(state)) return 'illegal_action'; // G2：补摸无牌则禁杠

  const count = handCounts(player.hand)[k] ?? 0;
  if (count === 4) return doAnGang(state, seat, kind, ctx, e);

  const pengIdx = player.melds.findIndex((m) => m.type === 'peng' && m.kind === kind);
  if (pengIdx >= 0 && count >= 1) return doBuGang(state, seat, kind, ctx, e);
  return 'illegal_action';
}

/** 暗杠（`02` §4.3）。D-07：不可被抢杠。D-14：只公开「谁杠了」。 */
function doAnGang(
  state: GameState,
  seat: SeatIndex,
  kind: TileKind,
  ctx: EngineContext,
  e: Emit,
): GameState {
  const player = state.players[seat];
  const tiles = takeKind(player.hand, keyOfKind(kind), 4);
  if (tiles === null) {
    throw new InvariantViolationError(`暗杠时手中不足 4 张 ${kind}`, { seat, kind });
  }
  let hand = player.hand;
  for (const id of tiles) {
    const rest = removeTile(hand, id);
    if (rest === null) throw new InvariantViolationError(`暗杠取牌失败: ${id}`, { seat, id });
    hand = rest;
  }

  const meld: Meld = {
    type: 'gang_an',
    kind,
    tiles,
    fromSeat: null,
    claimedTileId: null,
    // 写「补摸后」的 turnCount，使 isGangFlower() 能无歧义地判杠上花
    atTurn: state.turnCount + 1,
  };
  let s = withPlayer(state, seat, { hand, melds: [...player.melds, meld] });
  s = drawTile(s, seat, s.rules.config.gang.drawFromTail, ctx, e);

  emit(e, 'gang_made', toSeat(seat), { seat, type: 'gang_an', kind, tiles });
  // D-14：他家只知道「座位 s 暗杠了」，不知道杠的是什么
  emit(e, 'gang_made', toOthers(seat), { seat, type: 'gang_an' });
  return applyLiveScores(s);
}

/**
 * 巴杠（补杠）。可被抢杠胡（`02` §5），所以**先开窗口、后落杠**：
 * 第 4 张在窗口期间仍留在手里，被抢时不必回滚已改写的副露。
 */
function doBuGang(
  state: GameState,
  seat: SeatIndex,
  kind: TileKind,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  const picked = takeKind(state.players[seat].hand, keyOfKind(kind), 1);
  const tileId = picked?.[0];
  if (tileId === undefined) return 'illegal_action';

  const cfg = state.rules.config;
  if (cfg.qiangGang.enabled && cfg.qiangGang.allowBaGang) {
    const options = buildQiangGangOptions(state, seat, tileId);
    if (options.length > 0) {
      const deadlineAt = ctx.now + cfg.timeout.qiangGangMs;
      const next: GameState = {
        ...state,
        pendingClaims: {
          tileId,
          fromSeat: seat,
          trigger: 'gang_bu',
          options,
          responses: {},
          deadlineAt,
        },
        deadlineAt,
      };
      openClaimWindow(next, options, e);
      return next;
    }
  }
  return completeBuGang(state, seat, tileId, ctx, e);
}

/** 抢杠胡候选（`02` §5）。只有「胡」这一种响应。 */
function buildQiangGangOptions(
  state: GameState,
  gangSeat: SeatIndex,
  tileId: TileId,
): readonly ClaimOption[] {
  const out: ClaimOption[] = [];
  for (let s = 0 as SeatIndex; s < 4; s = (s + 1) as SeatIndex) {
    if (s === gangSeat) continue;
    const p = state.players[s];
    if (p.hasWon) continue;
    const win = checkWin({
      hand: p.hand,
      melds: p.melds,
      winningTile: tileId,
      winKind: 'qiang_gang',
      voidSuit: p.voidSuit,
      rules: state.rules,
      isLastTile: wallRemaining(state) === 0,
      gen: null,
    });
    if (win.isWin) out.push({ seat: s, actions: ['hu', 'pass'], gangKinds: [] });
  }
  return out;
}

/** 抢杠窗口无人应或未开窗：巴杠正式成立，碰子升格为杠并补摸。 */
function completeBuGang(
  state: GameState,
  seat: SeatIndex,
  tileId: TileId,
  ctx: EngineContext,
  e: Emit,
): GameState {
  const kind = kindOfTileId(tileId);
  const player = state.players[seat];
  const pengIdx = player.melds.findIndex((m) => m.type === 'peng' && m.kind === kind);
  const peng = pengIdx >= 0 ? player.melds[pengIdx] : undefined;
  if (peng === undefined) {
    throw new InvariantViolationError(`巴杠时找不到对应的碰: ${kind}`, { seat, kind });
  }
  const hand = removeTile(player.hand, tileId);
  if (hand === null) {
    throw new InvariantViolationError(`巴杠时手中没有第 4 张: ${tileId}`, { seat, tileId });
  }

  const melds = player.melds.slice();
  melds[pengIdx] = {
    ...peng,
    type: 'gang_bu',
    tiles: [...peng.tiles, tileId].sort((a, b) => a - b),
    atTurn: state.turnCount + 1,
  };

  let s = withPlayer({ ...state, pendingClaims: null }, seat, { hand, melds });
  s = drawTile(s, seat, s.rules.config.gang.drawFromTail, ctx, e);
  emit(e, 'gang_made', ALL, { seat, type: 'gang_bu', kind, tileId });
  return applyLiveScores(s);
}

/* ─────────────── 自摸 ─────────────── */

function doZimo(
  state: GameState,
  seat: SeatIndex,
  tileId: TileId,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  const player = state.players[seat];
  if (player.hand.length !== expectedHandSize(player.melds)) return 'illegal_action';

  const rest = removeTile(player.hand, tileId);
  if (rest === null) return 'tile_not_owned';

  const winKind: WinKind = isGangFlower(player, state.turnCount) ? 'gang_shang_hua' : 'zimo';
  const win = checkWin({
    hand: rest,
    melds: player.melds,
    winningTile: tileId,
    winKind,
    voidSuit: player.voidSuit,
    rules: state.rules,
    isLastTile: wallRemaining(state) === 0,
    gen: null,
  });
  if (!win.isWin) return 'illegal_action';

  // 自摸不搬牌：胡牌张本就在手中，手牌冻结在 3n+2。
  let s = recordWin(state, seat, {
    kind: winKind,
    fromSeat: null,
    winningTile: tileId,
    fanTags: win.fanTags,
    rawFan: win.rawFan,
    cappedFan: win.cappedFan,
    atTurn: state.turnCount,
  }, e);
  s = { ...s, deadlineAt: null };

  if (s.winnersInOrder.length >= 3) return enterSettlement(s, e);
  return advanceTurnFrom(s, seat, ctx, e);
}

/* ─────────────── 响应窗口 ─────────────── */

function applyClaim(
  state: GameState,
  action: Action,
  ctx: EngineContext,
  e: Emit,
): GameState | RejectReason {
  const pc = state.pendingClaims;
  if (pc === null) return 'wrong_phase';

  // 窗口到点：所有未作答的候选者一律视为 PASS（`02` §7.2）
  if (action.type === 'timeout') {
    const responses: Partial<Record<SeatIndex, ClaimActionType>> = { ...pc.responses };
    for (const o of pc.options) if (responses[o.seat] === undefined) responses[o.seat] = 'pass';
    return resolveClaims({ ...state, pendingClaims: { ...pc, responses } }, ctx, e);
  }

  const seat = action.seat;
  const opt = pc.options.find((o) => o.seat === seat);
  if (opt === undefined) return 'illegal_action';
  if (pc.responses[seat] !== undefined) return 'claim_window_closed';

  let choice: ClaimActionType;
  switch (action.type) {
    case 'pass':
      choice = 'pass';
      break;
    case 'hu':
      if (!opt.actions.includes('hu') || action.tileId !== pc.tileId) return 'illegal_action';
      choice = 'hu';
      break;
    case 'peng':
      if (!opt.actions.includes('peng') || action.tileId !== pc.tileId) return 'illegal_action';
      choice = 'peng';
      break;
    case 'gang':
      if (!opt.actions.includes('gang') || !opt.gangKinds.includes(action.kind)) {
        return 'illegal_action';
      }
      choice = 'gang';
      break;
    default:
      return 'illegal_action';
  }

  const responses: Partial<Record<SeatIndex, ClaimActionType>> = { ...pc.responses };
  responses[seat] = choice;
  const next: GameState = { ...state, pendingClaims: { ...pc, responses } };
  // 只回执给本人：广播「座位 2 过了」等于广播「座位 2 手里有那对牌」。
  emit(e, 'passed', toSeat(seat), { seat, choice });

  // 一炮多响必须集齐所有候选者的答复才能定序，所以一律等齐再裁决；
  // 提前结束由宿主的 timeout 负责，不在 core 里做启发式抢跑。
  if (!pc.options.every((o) => responses[o.seat] !== undefined)) return next;
  return resolveClaims(next, ctx, e);
}

/** 优先级裁决（`02` §7.1）：HU > GANG > PENG > PASS；同级按「离出牌者最近」。 */
function resolveClaims(state: GameState, ctx: EngineContext, e: Emit): GameState {
  const pc = state.pendingClaims;
  if (pc === null) return state;
  const { tileId, fromSeat, responses } = pc;

  const huSeats: SeatIndex[] = [];
  const gangSeats: SeatIndex[] = [];
  const pengSeats: SeatIndex[] = [];
  for (const o of pc.options) {
    const r = responses[o.seat];
    if (r === 'hu') huSeats.push(o.seat);
    else if (r === 'gang') gangSeats.push(o.seat);
    else if (r === 'peng') pengSeats.push(o.seat);
  }

  // `05` §4.2：以出牌者为原点顺时针取序
  const byDistance = (a: SeatIndex, b: SeatIndex): number =>
    (((a - fromSeat + 4) % 4) - ((b - fromSeat + 4) % 4));

  if (huSeats.length > 0) {
    const ordered = huSeats.slice().sort(byDistance);
    const winners = state.rules.config.xuezhan.multipleHu ? ordered : ordered.slice(0, 1);
    return resolveHu(state, winners, ctx, e);
  }
  if (gangSeats.length > 0) {
    const seat = gangSeats.slice().sort(byDistance)[0];
    if (seat !== undefined) return doMingGang(state, seat, fromSeat, tileId, ctx, e);
  }
  if (pengSeats.length > 0) {
    const seat = pengSeats.slice().sort(byDistance)[0];
    if (seat !== undefined) return doPeng(state, seat, fromSeat, tileId, ctx, e);
  }

  // 全部 PASS
  if (pc.trigger === 'gang_bu') {
    return completeBuGang({ ...state, pendingClaims: null }, fromSeat, tileId, ctx, e);
  }
  return advanceTurnFrom(state, fromSeat, ctx, e);
}

/**
 * 胡牌裁决。支持一炮多响（D-03）。
 *
 * 点炮张**不进任何胡家的手牌**：真实牌实体留在点炮者的弃牌河，
 * 每个胡家的 `WinRecord.winningTile` 都指向同一个 id（`05` §4.3 的虚拟胡牌张）。
 * 这样单响与多响共用一条路径，实体守恒也天然成立。
 */
function resolveHu(
  state: GameState,
  winners: readonly SeatIndex[],
  ctx: EngineContext,
  e: Emit,
): GameState {
  const pc = state.pendingClaims;
  if (pc === null) return state;
  const { tileId, fromSeat, trigger } = pc;

  const gangPao = trigger === 'discard' && isGangFlower(state.players[fromSeat], state.turnCount);
  const winKind: WinKind =
    trigger === 'gang_bu' ? 'qiang_gang' : gangPao ? 'gang_shang_pao' : 'dianpao';

  let s: GameState = markClaimed({ ...state, pendingClaims: null });

  if (trigger === 'gang_bu') {
    // 抢杠成立 ⇒ 杠不成立，第 4 张视同「打出」，落进杠牌者的弃牌河。
    const ganger = s.players[fromSeat];
    const hand = removeTile(ganger.hand, tileId);
    if (hand === null) {
      throw new InvariantViolationError('抢杠时杠牌者手中已无该牌', { fromSeat, tileId });
    }
    s = withPlayer(s, fromSeat, { hand, discards: [...ganger.discards, tileId] });
    s = { ...s, lastDiscard: { tileId, seat: fromSeat, atTurn: s.turnCount, claimed: true } };
  }

  for (const seat of winners) {
    const p = s.players[seat];
    const win = checkWin({
      hand: p.hand,
      melds: p.melds,
      winningTile: tileId,
      winKind,
      voidSuit: p.voidSuit,
      rules: s.rules,
      isLastTile: wallRemaining(s) === 0,
      gen: null,
    });
    if (!win.isWin) continue; // 理论不可达：开窗时已校验
    s = recordWin(s, seat, {
      kind: winKind,
      fromSeat,
      winningTile: tileId,
      fanTags: win.fanTags,
      rawFan: win.rawFan,
      cappedFan: win.cappedFan,
      // 同一 atTurn ⇒ currentPayableSeats 视多响胡家彼此「尚未胡」，各自向点炮者收
      atTurn: s.turnCount,
    }, e);
  }

  s = { ...s, deadlineAt: null };
  if (s.winnersInOrder.length >= 3) return enterSettlement(s, e);
  return advanceTurnFrom(s, fromSeat, ctx, e);
}

/** 记一次胡牌：置 `hasWon`、追加 `WinRecord`、刷新实时分、广播 `won`。 */
function recordWin(
  state: GameState,
  seat: SeatIndex,
  record: Omit<WinRecord, 'seat'>,
  e: Emit,
): GameState {
  const player = state.players[seat];
  let s = withPlayer(state, seat, {
    hasWon: true,
    winRecords: [...player.winRecords, { seat, ...record }],
    // X-3：已胡玩家手牌冻结，听牌信息不再有意义
    isTing: false,
    tingCandidates: null,
  });
  s = { ...s, winnersInOrder: [...s.winnersInOrder, seat] };

  emit(e, 'won', ALL, {
    seat,
    kind: record.kind,
    fromSeat: record.fromSeat,
    winningTile: record.winningTile,
    fanTags: record.fanTags,
    rawFan: record.rawFan,
    cappedFan: record.cappedFan,
    // X-4：胡牌者亮牌是规则要求的公开信息，受 xuezhan.revealHandOnWin 控制。
    // 键名刻意避开 hand/hands/handTiles——那三个是公开广播的禁字段。
    ...(s.rules.config.xuezhan.revealHandOnWin
      ? { revealedTiles: s.players[seat].hand, revealedMelds: s.players[seat].melds }
      : {}),
  });
  return applyLiveScores(s);
}

/* ─────────────── 碰 / 明杠 ─────────────── */

function doPeng(
  state: GameState,
  seat: SeatIndex,
  fromSeat: SeatIndex,
  tileId: TileId,
  ctx: EngineContext,
  e: Emit,
): GameState {
  const kind = kindOfTileId(tileId);
  const player = state.players[seat];
  const picked = takeKind(player.hand, kindIndexFromTileId(tileId), 2);
  if (picked === null) {
    throw new InvariantViolationError(`碰牌时手中不足 2 张 ${kind}`, { seat, kind });
  }
  let hand = player.hand;
  for (const id of picked) {
    const rest = removeTile(hand, id);
    if (rest === null) throw new InvariantViolationError(`碰牌取牌失败: ${id}`, { seat, id });
    hand = rest;
  }

  const meld: Meld = {
    type: 'peng',
    kind,
    tiles: [...picked, tileId].sort((a, b) => a - b),
    fromSeat,
    claimedTileId: tileId,
    atTurn: state.turnCount,
  };

  let s = takeFromRiver(state, fromSeat, tileId);
  s = withPlayer(s, seat, { hand, melds: [...player.melds, meld] });
  s = markClaimed({ ...s, pendingClaims: null, turn: seat });
  s = { ...s, deadlineAt: ctx.now + s.rules.config.timeout.discardMs };

  emit(e, 'peng_made', ALL, { seat, fromSeat, kind, tileId });
  emit(e, 'turn_changed', ALL, { seat });
  return s;
}

function doMingGang(
  state: GameState,
  seat: SeatIndex,
  fromSeat: SeatIndex,
  tileId: TileId,
  ctx: EngineContext,
  e: Emit,
): GameState {
  const kind = kindOfTileId(tileId);
  const player = state.players[seat];
  const picked = takeKind(player.hand, kindIndexFromTileId(tileId), 3);
  if (picked === null) {
    throw new InvariantViolationError(`明杠时手中不足 3 张 ${kind}`, { seat, kind });
  }
  let hand = player.hand;
  for (const id of picked) {
    const rest = removeTile(hand, id);
    if (rest === null) throw new InvariantViolationError(`明杠取牌失败: ${id}`, { seat, id });
    hand = rest;
  }

  const meld: Meld = {
    type: 'gang_ming',
    kind,
    tiles: [...picked, tileId].sort((a, b) => a - b),
    fromSeat,
    claimedTileId: tileId,
    atTurn: state.turnCount + 1,
  };

  let s = takeFromRiver(state, fromSeat, tileId);
  s = withPlayer(s, seat, { hand, melds: [...player.melds, meld] });
  s = markClaimed({ ...s, pendingClaims: null, turn: seat });
  s = drawTile(s, seat, s.rules.config.gang.drawFromTail, ctx, e);

  emit(e, 'gang_made', ALL, { seat, type: 'gang_ming', kind, fromSeat, tileId });
  return applyLiveScores(s);
}

/* ─────────────── 结算 ─────────────── */

/**
 * 进入终局结算。
 *
 * 两种触发：三家已胡（`THREE_WON`）或牌墙摸尽（`WALL_EXHAUSTED`），
 * 判定与查大叫 / 呼叫转移的执行条件都在 `settle.ts` 里，此处只负责搬运。
 *
 * 终态直接落在 `finished`：`settlement` 是纯计算过渡态，没有任何动作能从它继续推进，
 * 停在那里只会给宿主留一个「还需要再敲一下」的假象。
 */
function enterSettlement(state: GameState, e: Emit): GameState {
  const staged: GameState = {
    ...state,
    phase: 'settlement',
    pendingClaims: null,
    deadlineAt: null,
  };
  const settlement = calculateScore(staged);
  const p = staged.players;
  const players: Seats<PlayerState> = [
    { ...p[0], score: settlement.netScores[0] },
    { ...p[1], score: settlement.netScores[1] },
    { ...p[2], score: settlement.netScores[2] },
    { ...p[3], score: settlement.netScores[3] },
  ];

  emit(e, 'settled', ALL, {
    transfers: settlement.transfers,
    netScores: settlement.netScores,
    dajiaoSeats: settlement.dajiaoSeats,
    flowerPigSeats: settlement.flowerPigSeats,
    gangRefunds: settlement.gangRefunds,
  });
  emit(e, 'game_finished', ALL, { reason: endReasonOf(staged) });

  return { ...staged, phase: 'finished', players, settlement };
}
